import { PublicInvitationLayout } from "../../components/PublicInvitationLayout/PublicInvitationLayout";
import { useParams } from "react-router-dom";
import InvitationService from "../../services/InvitationService";
import Loader from "../../components/Loader/Loader";
import React, { useState, useEffect } from "react";
import { date, formatPeriod } from "../../utils/date";
import { getInvitationTypeKey, InvitationType, InvitationTypeText } from "../../enums/invitationTypes";
import { InvitationTypeIcons } from "../../utils/invitationTypeIcons";
import Logo from "../../components/Logo/Logo";
import { CarFront, Clock, Clock3, Clock7, MapPin, Phone, User, Check, X, UserStar, ScanFace, Key } from "lucide-react";
import axios from "axios";
import { Map } from "../../components/Map/Map";
import { joinWithCommaAnd, slugify } from "../../utils/string";
import ImagesImportContainer from "../../components/ImagesImportContainer/ImagesImportContainer";
import "./PublicInvitation.css";
import { GoArrowSwitch } from "react-icons/go";
import ResponseAlerts from "../../functions/ResponseAlerts";
import { AccessIcons } from "../../utils/accessIcons";
import { MdDirections } from "react-icons/md";
import { TiDirections } from "react-icons/ti";
import MqttService from "../../services/MqttService";
import Input from "../../components/Input/Input";
import ImagesService from "../../services/ImagesService";
import { format } from "date-fns";
import { AccessButton } from "./AccessButton";

export const PublicInvitation = () => {
   const { token } = useParams();
   const [isLoading, setIsLoading] = useState(true);
   const [invitation, setInvitation] = useState();
   const [showMap, setShowMap] = useState(false);
   const [canAccept, setCanAccept] = useState(false);
   const [actionLoading, setActionLoading] = useState(false);
   const [urlImg, setUrlImg] = useState(null);
   const [photoFile, setPhotoFile] = useState(null);
   const [userLocation, setUserLocation] = useState(null);
   const [insideRadius, setInsideRadius] = useState(null);
   const [plate, setPlate] = useState(null);

   useEffect(() => {
      if (invitation) {
         document.title = `Convite para ${invitation.name}`;
         setPlate(invitation.plate || "");

         if (invitation.type === InvitationType.VIRTUAL_KEY && invitation.enforceGeolocationCheck) {
            if (!navigator.geolocation) return;

            const watchId = navigator.geolocation.watchPosition(
               (pos) => {
                  console.log(pos);
                  setUserLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude })
               },
               () => { },
               { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
            );

            return () => {
               navigator.geolocation.clearWatch(watchId);
            };
         }
      }
   }, [invitation]);

   useEffect(() => {
      setCanAccept(!!urlImg);
   }, [urlImg])

   async function handleAccept() {
      try {
         setActionLoading(true);
         const imageUrl = invitation.type === InvitationType.FACIAL ? await handleImportPhotoSubmission() : null;
         if (invitation.allowGuestToFillPlate && (!plate || plate.trim().length === 0)) {
            ResponseAlerts({
               response: {
                  status: 400,
               },
               errorMessage: "Por favor, informe a placa do veículo.",
            });
            return;
         }
         const response = await axios.post(`access-control-service/invitations/token/${token}/accept`, {
            urlPhoto: imageUrl,
            plate: plate,
         });
         if (response.status === 200 || response.status === 201) {
            setInvitation(prev => ({ ...prev, status: 'Accepted' }));
         }
      } catch (error) {
         ResponseAlerts({
            response: error.response,
            errorMessage: "Erro ao aceitar convite",
         });
      } finally {
         setActionLoading(false);
      }
   }

   async function handleImportPhotoSubmission() {
      const newUrl = `${slugify(invitation.name)}.jpeg`;
      const formData = new FormData();
      formData.append("invitationToken", token);
      formData.append("file", photoFile);
      formData.append("imageUrl", newUrl);
      const response = await ImagesService.storeInvitationImage(formData);
      setUrlImg(response.data.payload.id);
      return response.data.payload.id;
   }

   async function handleReject() {
      try {
         setActionLoading(true);
         const response = await axios.post(`access-control-service/invitations/token/${token}/deny`);
         if (response.status === 200 || response.status === 201) {
            setInvitation(prev => ({ ...prev, status: 'Canceled' }));
         }
      } catch (error) {
         console.error(error);
         ResponseAlerts({
            response: error.response,
            errorMessage: "Erro ao recusar convite",
         });
      } finally {
         setActionLoading(false);
      }
   }

   async function fetchInvitation() {
      try {
         setIsLoading(true);
         const response = await InvitationService.getInvitationByToken(token);
         const invit = response.data;
         if (response.status === 200) {
            setInvitation(invit);
         } else {
            setInvitation(null);
         }
         setCanAccept(
            (invit.type === InvitationType.FACIAL && invit.status !== 'Accepted') ||
            (invit.type !== InvitationType.FACIAL)
         );
      } catch (error) {
         console.error(error);
      } finally {
         setIsLoading(false);
      }
   }

   useEffect(() => {
      fetchInvitation();
   }, [token]);

   if (isLoading) {
      return (
         <PublicInvitationLayout>
            <div className="flex items-center justify-center py-12">
               <Loader />
            </div>
         </PublicInvitationLayout>
      );
   }

   if (!invitation) {
      return (
         <PublicInvitationLayout>
            <div className="w-full max-w-md bg-(--containerBackgroundColor)! border! border-(--borderColor)! rounded-2xl! p-8! text-center shadow-lg relative transition-colors duration-200">
               <div className="text-red-500! text-5xl! mb-4! flex justify-center">⚠️</div>
               <h2 className="text-2xl! font-bold text-(--textColor)! mb-2!">Convite Inválido</h2>
               <p className="text-(--textColor)! text-[14px]! leading-[1.5]!">
                  Não foi possível encontrar as informações deste convite.<br /> Verifique o link ou entre em contato com quem te convidou.
               </p>
            </div>
         </PublicInvitationLayout>
      );
   }

   const IconComponent = InvitationTypeIcons[getInvitationTypeKey(invitation.type)] || null;

   const coordinates = {
      latitude: invitation.customCenterLatitude ?? -30.058792048426856,
      longitude: invitation.customCenterLongitude ?? -51.21816782593429
   };

   return (
      <PublicInvitationLayout>
         <div className="access-invitation max-w-full w-xl bg-(--containerBackgroundColor) shadow-xl border! border-(--borderColor)! rounded-2xl relative p-10! mb-10! **:select-none!">
            <div className="absolute -left-5 top-[115px] -translate-y-1/2 size-10 bg-(--bodyBackgroundColor) rounded-full border! bborder-dashed border-(--borderColor)!">
               <div className="absolute w-[calc(100%+4px)] top-1/2 -translate-y-1/2 -left-[67%] aspect-square bg-(--bodyBackgroundColor)"></div>
            </div>
            <div className="absolute -right-5 top-[115px] -translate-y-1/2 size-10 bg-(--bodyBackgroundColor) rounded-full border! bborder-dashed border-(--borderColor)!">
               <div className="absolute w-[calc(100%+4px)] top-1/2 -translate-y-1/2 -right-[67%] aspect-square bg-(--bodyBackgroundColor)"></div>
            </div>

            <div className="flex items-center justify-between gap-2 pb-10! relative">
               <div className="absolute -bottom-0 -left-[10px] w-[calc(100%+20px)] border-b-2! border-dashed! border-(--borderColor)!"></div>
               <div className="flex flex-row gap-4 items-center">
                  {invitation.organization ? (
                     <Logo
                        cached={true}
                        cacheKey={`organization-logo`}
                        showIcon={false}
                        imgId={invitation.organization.urlLogo}
                        alt="logo"
                        className="w-28! max-h-28! m-0! header-bar-logo"
                     />
                  ) : (
                     <Logo
                        showIcon={false}
                        alt="logo"
                        className="w-28! max-h-28! m-0! header-bar-logo"
                     />
                  )}
                  <div className="flex flex-col">
                     <p className="text-[15px]! text-(--textColor) uppercase tracking-widest mb-2! font-bold!">
                        Convite de Acesso
                     </p>
                     <h2 className="text-2xl opacity-80 text-(--textColor)">
                        Você recebeu um convite para {joinWithCommaAnd(invitation.units?.map(u => u.name)) || "Bravas Cloud"}
                     </h2>
                  </div>
               </div>
               {IconComponent && (
                  <div className="inline-flex items-center gap-2 px-3! py-1! bg-(--backgroundCard) text-(--primaryColor) rounded-full text-xs font-bold! tracking-wider uppercase">
                     <IconComponent className="text-2xl!" />
                     <span className="font-bold!">{InvitationTypeText[invitation.type] || 'Convite'}</span>
                  </div>
               )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 mt-2!">
               <div className="border-b! border-b-(--borderColor)! py-6!">
                  <p className="flex flex-row gap-1.5 items-center text-(--textColor) opacity-70 text-sm mb-2.5!">
                     <User size={15} />
                     Convidado
                  </p>
                  <p className="text-(--textColor) text-[15px]! font-medium">{invitation.name}</p>
               </div>

               <div className="border-b! border-b-(--borderColor)! py-6!">
                  <p className="flex flex-row gap-1.5 items-center text-(--textColor) opacity-70 text-sm mb-2.5!">
                     <UserStar size={15} />
                     Enviado por
                  </p>
                  <p className="text-(--textColor) text-[15px]! font-medium">
                     Administrador
                  </p>
               </div>

               <div className="col-span-2 border-b! border-b-(--borderColor)! py-6!">
                  <p className="flex flex-row gap-1.5 items-center text-(--textColor) opacity-70 text-sm mb-2.5!">
                     <MapPin size={15} />
                     Localização
                  </p>
                  <p className="text-(--textColor) text-[15px]! font-medium mb-2!">
                     {`${invitation.address?.streetName}, ${invitation.address?.streetNumber} - ${invitation.address?.district} - ${invitation.address?.city} - ${invitation.address?.state}`}
                  </p>
                  <div className="flex items-center gap-5!">
                     <p className="text-(--textColor) text-[15px]! font-medium underline cursor-pointer hover:text-(--primaryColor)! duration-150" onClick={e => setShowMap(prev => !prev)}>
                        {showMap ? 'Esconder mapa' : 'Ver no mapa'}
                     </p>
                     <a
                        className="inline-flex gap-1! items-center text-(--textColor) text-[15px]! font-medium underline cursor-pointer hover:text-(--primaryColor)! duration-150"
                        href={`https://www.google.com/maps/dir/?api=1&destination=${coordinates.latitude},${coordinates.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                     >
                        <TiDirections size={20} />
                        Ver rotas
                     </a>
                  </div>
                  <div className={`w-full aspect-video rounded-xl! overflow-hidden mt-4! ${showMap ? '' : 'hidden!'}`}>
                     <Map
                        coordinates={coordinates}
                        trackUserPosition={invitation.type === InvitationType.VIRTUAL_KEY && invitation.enforceGeolocationCheck && invitation.status === 'Accepted'}
                        onUserInsideAny={(inside) => {
                           setInsideRadius(inside);
                        }}
                        radius={invitation.geolocationRadius} />
                  </div>
               </div>

               <div className={`border-b! border-b-(--borderColor)! py-6!`}>
                  <p className="flex flex-row gap-1.5 items-center text-(--textColor) opacity-70 text-sm mb-2.5!">
                     <Clock3 size={15} />
                     Início
                  </p>
                  <p className="text-(--textColor) text-[15px]! font-medium">
                     {date("d/m/Y àz H:i", invitation.startDate).replace('z', 's')}
                  </p>
               </div>

               <div className={`border-b! border-b-(--borderColor)! py-6!`}>
                  <p className="flex flex-row gap-1.5 items-center text-(--textColor) opacity-70 text-sm mb-2.5!">
                     <Clock7 size={15} />
                     Término
                  </p>
                  <p className="text-(--textColor) text-[15px]! font-medium">
                     {date("d/m/Y àz H:i", invitation.endDate).replace('z', 's')}
                  </p>
               </div>

               {invitation.type === InvitationType.VEHICLE && (
                  <div className="py-6! col-span-2!">
                     <p className="flex flex-row gap-1.5 items-center text-(--textColor) opacity-70 text-sm mb-2.5!">
                        <CarFront size={15} />
                        Placa
                     </p>
                     {
                        (invitation.status === 'Accepted' || !invitation.allowGuestToFillPlate) ? (
                           <p className="text-(--textColor) text-[15px]! font-medium">
                              {invitation.plate?.toUpperCase()}
                           </p>
                        ) : (
                           <Input
                              id="plate"
                              name="plate"
                              value={plate}
                              onChange={(e) => {
                                 const v = e.target.value.toUpperCase();
                                 setPlate(v);
                                 invitation.plate = v;
                              }}
                              placeholder="Insira a placa do veículo"
                           />
                        )
                     }
                  </div>
               )}

               {invitation.type === InvitationType.VIRTUAL_KEY && (
                  <div className="border-b! border-b-(--borderColor)! py-6!">
                     <p className="flex flex-row gap-1.5 items-center text-(--textColor) opacity-70 text-sm mb-2.5!">
                        <Key size={15} />
                        Créditos
                     </p>
                     <p className="text-(--textColor) text-[15px]! font-medium">
                        {invitation.credits} {invitation.credits === 1 ? 'crédito' : 'créditos'} restante{invitation.credits !== 1 ? 's' : ''}
                     </p>
                  </div>
               )}

               {(invitation.type === InvitationType.VIRTUAL_KEY && invitation.status === 'Accepted') && (
                  <div className="col-span-2 py-6!">
                     <p className="flex flex-row gap-1.5 items-center text-(--textColor) opacity-70 text-sm mb-2.5!">
                        <GoArrowSwitch size={15} />
                        Acessos
                     </p>
                     <div className="grid sm:grid-cols-2 gap-3! mt-3!">
                        {invitation.accessModules?.filter(m => m.name)?.map((access, idx) => (
                           <AccessButton
                              disabled={(invitation.enforceGeolocationCheck && !insideRadius) || invitation.credits === 0}
                              key={idx}
                              access={access}
                              token={token}
                              userLocation={userLocation}
                              updateInvitation={setInvitation}
                           />
                        ))}
                     </div>
                  </div>
               )}

               {invitation.type === InvitationType.FACIAL && invitation.status !== 'Accepted' && (
                  <div className="col-span-2 py-6! mb-10!">
                     <p className="flex flex-row gap-1.5 items-center text-(--textColor) opacity-70 text-sm mb-2.5!">
                        <ScanFace size={15} />
                        Foto
                     </p>
                     <p className="text-(--textColor) text-[14px]! leading-[1.3]! opacity-70">
                        Tire uma foto do seu rosto. <b className="font-bold!">Atenção</b>, o ambiente deve estar bem iluminado e seu rosto deve estar sem acessórios, como óculos e máscaras.
                     </p>
                     <div className={`relative w-[calc(100%-30px)] aspect-video mb-8! mt-3! rounded-lg border-2! border-dashed! border-(--borderColor)! ${!urlImg ? 'empty-face' : ''}`}>
                        {!urlImg && (<User size={120} className="absolute text-(--textColor) opacity-30 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />)}
                        <ImagesImportContainer
                           withReaders={false}
                           setUrlImg={(url) => setUrlImg(url)}
                           onFileChanged={(file) => setPhotoFile(file)}
                           urlImg={urlImg}
                           onlyImage={true}
                        />
                     </div>
                  </div>
               )}
            </div>

            {![InvitationType.FACIAL, InvitationType.VIRTUAL_KEY].includes(invitation.type) && (
               <div className="border-t-2 border-dashed border-(--borderColor) my-2!"></div>
            )}

            {invitation.status === 'Accepted' || invitation.status === 'Approved' ? (
               <div className="mt-6! p-4! bg-green-500/10 border border-green-500/20 rounded-xl text-green-500 flex items-center justify-center gap-2 font-bold transition-all duration-200">
                  <Check size={20} />
                  <span>Convite Aceito</span>
               </div>
            ) : invitation.status === 'Canceled' || invitation.status === 'Rejected' ? (
               <div className="mt-6! p-4! bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 flex items-center justify-center gap-2 font-bold transition-all duration-200">
                  <X size={20} />
                  <span>Convite Recusado</span>
               </div>
            ) : invitation.status === 'Finished' ? (
               <div className="mt-6! p-4! bg-gray-500/10 border border-gray-500/20 rounded-xl text-gray-500 flex items-center justify-center gap-2 font-bold transition-all duration-200">
                  <Clock size={20} />
                  <span>Convite Finalizado</span>
               </div>
            ) : (
               <div className="flex flex-row gap-4 mt-6 w-full">
                  <button
                     disabled={actionLoading}
                     onClick={handleReject}
                     className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-red-500/30 hover:border-red-500 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white font-bold rounded-xl transition-all duration-200 active:scale-98 cursor-pointer shadow-sm hover:shadow-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                     {actionLoading ? (
                        <Loader width={20} height={20} style={{ margin: 0 }} />
                     ) : (
                        <>
                           <X size={22} />
                           <span className="font-bold! text-[16px]!">Recusar</span>
                        </>
                     )}
                  </button>
                  <button
                     disabled={actionLoading || !canAccept}
                     onClick={handleAccept}
                     className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-all duration-200 active:scale-98 cursor-pointer shadow-md hover:shadow-green-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                     {actionLoading ? (
                        <Loader width={20} height={20} style={{ margin: 0 }} />
                     ) : (
                        <>
                           <Check size={22} />
                           <span className="font-bold! text-[16px]!">Aceitar</span>
                        </>
                     )}
                  </button>
               </div>
            )}
         </div>
      </PublicInvitationLayout>
   );
};